import { useEffect, useState } from 'react';
import { useFetchReservations, useReservations } from '@repo/domain-service';
import { tr } from '@repo/locale';
import { TestId } from '@repo/test-id';
import { toast } from '@repo/toast';
import { useNavigate } from 'react-router-dom';

import PaymentBlikWaitContents from '@features/reservation/components/payment/PaymentBlikWaitContents';
import PaymentMethodsContents from '@features/reservation/components/payment/PaymentMethodsContents';
import { type ExtensionPayment, type PaymentResultLocationState } from '@features/reservation/types';

import PleaseWaitAnimation from '@shared/components/PleaseWaitAnimation';

import { ROUTE_PATHS } from '@router/paths';
import { RouteName } from '@router/routeNames';

const CONFIRMATION_POLL_INTERVAL_MILLISECONDS = 3_000;
// Mirrors the backend's `reservation.extension_lock.ttl_minutes`: once the hold
// lapses unpaid, the extension can no longer be confirmed.
const CONFIRMATION_TIMEOUT_MILLISECONDS = 5 * 60 * 1_000;

const testId = TestId.root('reservation.paymentExtension');

const EXTENSION_PAYMENT_RESULT: PaymentResultLocationState = { product: 'parkingSpotRentalExtension' };

interface Props {
  extensionPayment: ExtensionPayment;
}

/**
 * `/reservation/payment` for an extension of an active reservation. The
 * backend's `Reservation.order` can't point at the extension's own payment, so a
 * BLIK payment is confirmed by watching the span's end time reach the paid-for
 * one instead.
 */
export default function PaymentExtensionContents({ extensionPayment }: Props) {
  const navigate = useNavigate();
  const fetchReservations = useFetchReservations();
  const { reservations, loadingReservations } = useReservations({ id: extensionPayment.reservationId });
  const [awaitingConfirmation, setAwaitingConfirmation] = useState<boolean>(false);
  const reservation = reservations[0];

  useEffect(() => {
    if (loadingReservations || reservation) return;

    toast.danger(tr('reservations:extension_error.reservation_not_found'), testId.append('noReservationToast'));
    navigate(ROUTE_PATHS[RouteName.ACCOUNT_RESERVATIONS], { replace: true });
  }, [loadingReservations, reservation, navigate]);

  useEffect(() => {
    if (!awaitingConfirmation) return;

    const pollInterval = setInterval(() => {
      void (async () => {
        try {
          const [latestReservation] = await fetchReservations({ id: extensionPayment.reservationId });
          const extendedSpan = latestReservation?.spans.find(
            (reservationSpan) => reservationSpan.id === extensionPayment.reservationSpanId
          );
          if (extendedSpan && extendedSpan.endAt.getTime() >= extensionPayment.newEndAt.getTime()) {
            navigate(ROUTE_PATHS[RouteName.RESERVATION_SUCCESS], { replace: true, state: EXTENSION_PAYMENT_RESULT });
          }
        } catch {
          // A failed poll is retried on the next tick; the timeout below ends the wait.
        }
      })();
    }, CONFIRMATION_POLL_INTERVAL_MILLISECONDS);

    const confirmationTimeout = setTimeout(() => {
      navigate(ROUTE_PATHS[RouteName.RESERVATION_FAILURE], { replace: true, state: EXTENSION_PAYMENT_RESULT });
    }, CONFIRMATION_TIMEOUT_MILLISECONDS);

    return () => {
      clearInterval(pollInterval);
      clearTimeout(confirmationTimeout);
    };
    // Runs only when the wait starts; fetchReservations is recreated each render
    // and must not restart the poll.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [awaitingConfirmation, extensionPayment, navigate]);

  const handleBack = () => {
    navigate(-1);
  };

  const handleExtensionPaymentInitiated = () => {
    setAwaitingConfirmation(true);
  };

  if (loadingReservations) return <PleaseWaitAnimation testId={testId} />;

  if (!reservation) return null;

  if (awaitingConfirmation) return <PaymentBlikWaitContents onGoBack={handleBack} />;

  return (
    <PaymentMethodsContents
      reservation={reservation}
      extensionPayment={extensionPayment}
      onExtensionPaymentInitiated={handleExtensionPaymentInitiated}
    />
  );
}
